/**
 * Foto do membro (o avatar). A interface recorta e reduz a imagem escolhida para um
 * quadrado de `PHOTO_SIZE` px antes de enviar — a foto aparece no máximo com 68 px na
 * tela, então guardar o original só pesaria no banco e na rede —, e o servidor confere o
 * que recebeu com as mesmas regras (`photoError`), pelos bytes do arquivo e não pelo tipo
 * declarado.
 *
 * `Member.photo` é o endereço da imagem: o da API (`/api/v1/members/{id}/photo/{token}`)
 * ou, no modo demonstração, a própria imagem em `data:` URL.
 */

/** Tipos aceitos: os que todo navegador decodifica e o canvas sabe gerar. */
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export type PhotoType = (typeof PHOTO_TYPES)[number];

/** Tamanho máximo do arquivo escolhido (antes de reduzir). */
export const MAX_PHOTO_UPLOAD_BYTES = 2 * 1024 * 1024;

/** Lado do quadrado gravado, em px (o dobro do maior avatar, para telas de alta densidade). */
export const PHOTO_SIZE = 256;

/** Tamanho máximo da foto gravada (já reduzida); um 256 × 256 em WebP fica em ~10–30 KB. */
export const MAX_PHOTO_BYTES = 64 * 1024;

/** Lado máximo aceito pelo servidor: barra imagem enorme que pesaria ao decodificar. */
export const MAX_PHOTO_DIMENSION = 1024;

/** Quantos caracteres o base64 de `bytes` ocupa. */
export function base64Length(bytes: number): number {
  return Math.ceil(bytes / 3) * 4;
}

export const MAX_PHOTO_BASE64 = base64Length(MAX_PHOTO_BYTES);

export function isPhotoType(type: string): type is PhotoType {
  return (PHOTO_TYPES as readonly string[]).includes(type);
}

/** Erro do arquivo escolhido na tela (tipo e tamanho), antes de abrir o recorte. */
export function photoFileError(file: { size: number; type: string }): string | null {
  if (!isPhotoType(file.type)) return 'Escolha uma imagem JPG, PNG ou WebP.';
  if (file.size === 0) return 'O arquivo está vazio.';
  if (file.size > MAX_PHOTO_UPLOAD_BYTES) return 'A foto deve ter no máximo 2 MB.';

  return null;
}

export interface PhotoInfo {
  type: PhotoType;
  width: number;
  height: number;
}

/**
 * O tipo e as dimensões da imagem, lidos do cabeçalho do arquivo (PNG, JPEG e WebP com
 * VP8, VP8L ou VP8X). `null` quando não é uma imagem desses tipos ou está truncada.
 */
export function sniffPhoto(bytes: Uint8Array): PhotoInfo | null {
  return sniffPng(bytes) ?? sniffJpeg(bytes) ?? sniffWebp(bytes);
}

/** Erro da foto recebida pela API (o mesmo que a interface garante ao reduzir). */
export function photoError(bytes: Uint8Array, declared: string): string | null {
  const info = sniffPhoto(bytes);

  if (!info) return 'O arquivo não é uma imagem JPG, PNG ou WebP.';
  if (info.type !== declared) return 'O tipo informado não confere com o conteúdo da imagem.';
  if (bytes.length > MAX_PHOTO_BYTES) return 'A foto reduzida deve ter no máximo 64 KB.';
  if (info.width > MAX_PHOTO_DIMENSION || info.height > MAX_PHOTO_DIMENSION) {
    return `A foto deve ter no máximo ${MAX_PHOTO_DIMENSION} × ${MAX_PHOTO_DIMENSION} pixels.`;
  }

  return null;
}

/** A foto como `data:` URL (o modo demonstração guarda assim, no próprio estado). */
export function photoDataUrl(type: PhotoType, base64: string): string {
  return `data:${type};base64,${base64}`;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function startsWith(bytes: Uint8Array, prefix: readonly number[], at = 0): boolean {
  return bytes.length >= at + prefix.length && prefix.every((b, i) => bytes[at + i] === b);
}

function ascii(bytes: Uint8Array, at: number, text: string): boolean {
  return startsWith(
    bytes,
    [...text].map((c) => c.charCodeAt(0)),
    at
  );
}

const u16be = (b: Uint8Array, at: number) => (b[at]! << 8) | b[at + 1]!;
const u16le = (b: Uint8Array, at: number) => b[at]! | (b[at + 1]! << 8);
const u24le = (b: Uint8Array, at: number) => b[at]! | (b[at + 1]! << 8) | (b[at + 2]! << 16);
const u32be = (b: Uint8Array, at: number) => u16be(b, at) * 0x10000 + u16be(b, at + 2);

function sniffPng(b: Uint8Array): PhotoInfo | null {
  if (!startsWith(b, PNG_SIGNATURE) || b.length < 24 || !ascii(b, 12, 'IHDR')) return null;

  return { type: 'image/png', width: u32be(b, 16), height: u32be(b, 20) };
}

/** Marcadores SOF (início do quadro) do JPEG: trazem as dimensões. */
const JPEG_SOF = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);

function sniffJpeg(b: Uint8Array): PhotoInfo | null {
  if (!startsWith(b, [0xff, 0xd8, 0xff])) return null;

  let i = 2;

  while (i + 3 < b.length) {
    if (b[i] !== 0xff) return null;

    const marker = b[i + 1]!;

    // Preenchimento entre marcadores.
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    // Marcadores sem tamanho (TEM, RST0–7).
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2;
      continue;
    }
    // Início da imagem comprimida ou fim, sem ter achado o quadro.
    if (marker === 0xda || marker === 0xd9) return null;
    if (JPEG_SOF.has(marker)) {
      if (i + 9 > b.length) return null;

      return { type: 'image/jpeg', height: u16be(b, i + 5), width: u16be(b, i + 7) };
    }

    i += 2 + u16be(b, i + 2);
  }

  return null;
}

function sniffWebp(b: Uint8Array): PhotoInfo | null {
  if (!ascii(b, 0, 'RIFF') || !ascii(b, 8, 'WEBP') || b.length < 30) return null;

  // Com perdas: a assinatura 9D 01 2A do quadro-chave, e 14 bits de largura e de altura.
  if (ascii(b, 12, 'VP8 ')) {
    if (!startsWith(b, [0x9d, 0x01, 0x2a], 23)) return null;

    return { type: 'image/webp', width: u16le(b, 26) & 0x3fff, height: u16le(b, 28) & 0x3fff };
  }
  // Sem perdas: a assinatura 2F e 14 bits (menos 1) de cada lado, em sequência.
  if (ascii(b, 12, 'VP8L')) {
    if (b[20] !== 0x2f) return null;

    return {
      type: 'image/webp',
      width: 1 + (b[21]! | ((b[22]! & 0x3f) << 8)),
      height: 1 + ((b[22]! >> 6) | (b[23]! << 2) | ((b[24]! & 0x0f) << 10)),
    };
  }
  // Estendido (transparência, animação, metadados): 24 bits (menos 1) de cada lado.
  if (ascii(b, 12, 'VP8X')) {
    return { type: 'image/webp', width: 1 + u24le(b, 24), height: 1 + u24le(b, 27) };
  }

  return null;
}
